/* Private and limited products (Molinoteq)
 * Saves the client email and passwords as hidden cart attributes and reloads,
 * so the theme can check whitelist and purchases on the server.
 * The check itself never happens here: this file only stores the input.
 */
if (!customElements.get('limited-access-gate')) {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  class LimitedAccessGate extends HTMLElement {
    connectedCallback() {
      this.submitButton = this.querySelector('[data-limited-submit]');
      this.resetButton = this.querySelector('[data-limited-reset]');
      this.errorEl = this.querySelector('.limited-gate__error');

      // After a reload triggered by this form: show the form again instead of the old scroll position
      let returning = false;
      try {
        returning = sessionStorage.getItem('limitedScroll') === '1';
      } catch (error) {}
      if (returning && !LimitedAccessGate.scrolled && !this.closest('.product__add-cart--fixed')) {
        LimitedAccessGate.scrolled = true;
        try {
          sessionStorage.removeItem('limitedScroll');
        } catch (error) {}
        const target = this.closest('.limited-page') ? null : this;
        requestAnimationFrame(() => {
          if (target) target.scrollIntoView({ block: 'center' });
          else window.scrollTo(0, 0);
        });
      }

      if (this.submitButton) this.submitButton.addEventListener('click', () => this.submit());
      if (this.resetButton) this.resetButton.addEventListener('click', () => this.save({ _limited_email: '' }));

      this.querySelectorAll('input').forEach((input) => {
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          event.stopPropagation();
          this.submit();
        });
      });
    }

    submit() {
      const attributes = {};
      const inputs = this.querySelectorAll('[data-attr]');
      for (const input of inputs) {
        const isEmail = input.type === 'email';
        const value = isEmail ? input.value.trim().toLowerCase() : input.value.trim();
        if (isEmail && !EMAIL_RE.test(value)) {
          this.showError('Please enter a valid email address.');
          input.focus();
          return;
        }
        if (!isEmail && !value) {
          this.showError('Please enter the password from your invitation.');
          input.focus();
          return;
        }
        attributes[input.dataset.attr] = value;
      }
      this.save(attributes);
    }

    async save(attributes) {
      this.setBusy(true);
      const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
      try {
        const response = await fetch(`${base}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        try {
          sessionStorage.setItem('limitedScroll', '1');
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        } catch (error) {}
        window.scrollTo(0, 0);
        window.location.reload();
      } catch (error) {
        console.error('[limited-access]', error);
        this.setBusy(false);
        this.showError('Something went wrong. Please try again.');
      }
    }

    setBusy(busy) {
      [this.submitButton, this.resetButton].forEach((button) => {
        if (!button) return;
        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('limited-access-gate', LimitedAccessGate);

  /* After a limited piece was added via AJAX: lock its button until the page is reloaded,
     so the same piece cannot be added a second time from the open page. */
  const initProductLock = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') return;
      document.querySelectorAll('[data-limited-form]').forEach((wrapper) => {
        const idInput = wrapper.querySelector('input[name="id"]');
        if (!idInput || String(idInput.value) !== String(event.productVariantId)) return;
        const button = wrapper.querySelector('button[name="add"]');
        if (!button) return;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        const label = button.querySelector('span');
        if (label) label.textContent = 'In your cart';
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initProductLock);
  else initProductLock();
}

/* Cart page: keep the checkout button locked while the cart breaks the limited rules.
   The section "Limited cart check" is refreshed after every cart change. */
if (!window.__limitedCartGuard) {
  window.__limitedCartGuard = true;

  const isBlocked = () => !!document.querySelector('[data-limited-blocked]');

  const syncCheckout = () => {
    const checkout = document.getElementById('checkout');
    if (!checkout) return;
    const agree = document.getElementById('agree');
    const agreed = agree ? agree.checked : true;
    const empty = checkout.closest('.is-empty') !== null;
    checkout.disabled = isBlocked() || !agreed || empty;
    checkout.toggleAttribute('data-limited-locked', isBlocked());
  };

  const refreshSection = async () => {
    const container = document.getElementById('LimitedCartCheck');
    if (!container) return;
    try {
      const response = await fetch(`${window.location.pathname}?sections=${container.dataset.section}`);
      const json = await response.json();
      const html = new DOMParser().parseFromString(json[container.dataset.section], 'text/html');
      const fresh = html.getElementById('LimitedCartCheck');
      if (fresh) container.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.error('[limited-access]', error);
    }
    syncCheckout();
  };

  const init = () => {
    if (!document.getElementById('LimitedCartCheck')) return;
    syncCheckout();
    const agree = document.getElementById('agree');
    if (agree) agree.addEventListener('change', () => setTimeout(syncCheckout, 0));
    const footer = document.getElementById('main-cart-footer');
    if (footer) new MutationObserver(() => syncCheckout()).observe(footer, { childList: true, subtree: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => refreshSection());
    }
    const form = document.getElementById('cart');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (!isBlocked()) return;
        event.preventDefault();
        const block = document.querySelector('[data-limited-blocked]');
        if (block) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
